/* Mocked in-memory implementation of Documents Client for local development and UI wiring. */

import { IDocumentFoldersClient, IDocumentsClient } from './IDocumentsClient';
import {
  DocumentFolderDto,
  DocumentDto,
  DocumentVersionDto,
  FolderPermissionDto,
  FolderTreeNodeDto,
  DocumentSearchParams,
  DocumentSearchResult,
  CreateFolderRequest,
  UpdateFolderRequest,
  UploadDocumentRequest,
  UpdateDocumentRequest,
  UploadVersionRequest,
  SetFolderPermissionRequest,
  StoreTaskEvidenceRequest,
  DocumentScope,
  DocumentCategory,
  DocumentPermission,
  getDocumentTypeFromMime,
  ApiError,
} from './documentsTypes';
import {
  mockFolders,
  mockDocuments,
  mockDocumentVersions,
  mockPermissions,
  idGenerators,
} from './documentsMockData';

/* ---- Helper functions ---- */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/* ---- Document Folders Client (Mocked) ---- */
export class DocumentFoldersClientMocked implements IDocumentFoldersClient {
  /** Get root-level folders for a scope */
  async getRootFolders(scope?: DocumentScope, franchiseId?: number, storeId?: number): Promise<DocumentFolderDto[]> {
    await delay(200);
    return mockFolders.filter(f => {
      // Only root folders (no parent)
      if (f.parentFolderId !== null) return false;
      // Filter by scope if provided
      if (scope && f.scope !== scope) return false;
      // System-scoped folders are always visible to all
      if (f.scope === DocumentScope.System) return true;
      // If franchiseId filter is provided, only show matching franchise folders
      // If no franchiseId filter, show all franchise folders (for demo/dev purposes)
      if (franchiseId && f.franchiseId && f.franchiseId !== franchiseId) return false;
      // Filter by store if provided
      if (storeId && f.storeId !== storeId) return false;
      return true;
    });
  }

  /** Get a single folder by ID */
  async getFolder(id: number): Promise<DocumentFolderDto> {
    await delay(100);
    const folder = mockFolders.find(f => f.id === id);
    if (!folder) throw new ApiError(404, 'Folder not found');
    return folder;
  }

  /** Get ancestor folders (from root to parent) for breadcrumb navigation */
  async getAncestors(folderId: number): Promise<DocumentFolderDto[]> {
    await delay(100);
    const ancestors: DocumentFolderDto[] = [];
    let currentId: number | null | undefined = folderId;

    while (currentId) {
      const folder = mockFolders.find(f => f.id === currentId);
      if (!folder) break;
      currentId = folder.parentFolderId;
      if (currentId) {
        const parent = mockFolders.find(f => f.id === currentId);
        if (parent) ancestors.unshift(parent);
      }
    }

    return ancestors;
  }

  /** Search folders by name across all folders */
  async searchFolders(query: string, franchiseId?: number): Promise<DocumentFolderDto[]> {
    await delay(150);
    const q = query.toLowerCase();
    return mockFolders.filter(f => {
      // Match query against name, description, or path
      const matchesQuery =
        f.name.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.path.some(p => p.toLowerCase().includes(q));
      if (!matchesQuery) return false;

      // Filter by franchise if provided (but always include System folders)
      if (franchiseId && f.franchiseId && f.franchiseId !== franchiseId && f.scope !== DocumentScope.System) {
        return false;
      }
      return true;
    });
  }

  /** Get child folders of a parent folder */
  async getChildFolders(parentFolderId: number): Promise<DocumentFolderDto[]> {
    await delay(150);
    return mockFolders.filter(f => f.parentFolderId === parentFolderId);
  }

  /** Get folder tree for hierarchical navigation */
  async getFolderTree(rootFolderId?: number): Promise<FolderTreeNodeDto[]> {
    await delay(300);
    const buildTree = (parentId: number | null): FolderTreeNodeDto[] => {
      return mockFolders
        .filter(f => f.parentFolderId === parentId && (rootFolderId === undefined || f.id === rootFolderId || f.parentFolderId !== null))
        .map(folder => ({
          folder,
          children: buildTree(folder.id),
        }));
    };
    return rootFolderId ? buildTree(rootFolderId) : buildTree(null);
  }

  /** Create a new folder */
  async createFolder(request: CreateFolderRequest): Promise<DocumentFolderDto> {
    await delay(200);
    const parentPath = request.parentFolderId
      ? mockFolders.find(f => f.id === request.parentFolderId)?.path ?? []
      : [];

    const newFolder: DocumentFolderDto = {
      id: idGenerators.nextFolderId++,
      name: request.name,
      description: request.description,
      parentFolderId: request.parentFolderId ?? null,
      scope: request.scope,
      category: request.category,
      franchiseId: request.franchiseId,
      storeId: request.storeId,
      taskTemplateId: request.taskTemplateId,
      path: [...parentPath, request.name],
      childFolderCount: 0,
      documentCount: 0,
      effectivePermissions: [
        DocumentPermission.View,
        DocumentPermission.Upload,
        DocumentPermission.Download,
        DocumentPermission.Edit,
        DocumentPermission.Delete,
        DocumentPermission.ManagePermissions,
        DocumentPermission.CreateFolder,
      ],
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
    };
    mockFolders.push(newFolder);

    // Update parent folder child count
    if (request.parentFolderId) {
      const parent = mockFolders.find(f => f.id === request.parentFolderId);
      if (parent) parent.childFolderCount++;
    }

    return newFolder;
  }

  /** Update folder metadata */
  async updateFolder(id: number, request: UpdateFolderRequest): Promise<DocumentFolderDto> {
    await delay(200);
    const folder = mockFolders.find(f => f.id === id);
    if (!folder) throw new ApiError(404, 'Folder not found');

    folder.name = request.name;
    folder.description = request.description;
    folder.category = request.category;
    folder.updatedAt = new Date().toISOString();

    return folder;
  }

  /** Delete a folder (must be empty) */
  async deleteFolder(id: number): Promise<void> {
    await delay(200);
    const index = mockFolders.findIndex(f => f.id === id);
    if (index === -1) throw new ApiError(404, 'Folder not found');

    const folder = mockFolders[index];
    if (folder.childFolderCount > 0 || folder.documentCount > 0) {
      throw new ApiError(400, 'Cannot delete folder with contents');
    }

    mockFolders.splice(index, 1);

    // Update parent folder child count
    if (folder.parentFolderId) {
      const parent = mockFolders.find(f => f.id === folder.parentFolderId);
      if (parent) parent.childFolderCount--;
    }
  }

  /** Get folder permissions */
  async getFolderPermissions(folderId: number): Promise<FolderPermissionDto[]> {
    await delay(150);
    return mockPermissions.filter(p => p.folderId === folderId);
  }

  /** Set folder permission */
  async setFolderPermission(folderId: number, request: SetFolderPermissionRequest): Promise<FolderPermissionDto> {
    await delay(200);
    const existing = mockPermissions.find(p =>
      p.folderId === folderId &&
      p.roleId === request.roleId &&
      p.userId === request.userId &&
      p.franchiseId === request.franchiseId &&
      p.storeId === request.storeId,
    );

    if (existing) {
      existing.permissions = request.permissions;
      return existing;
    }

    const newPermission: FolderPermissionDto = {
      id: idGenerators.nextPermissionId++,
      folderId,
      roleId: request.roleId,
      userId: request.userId,
      franchiseId: request.franchiseId,
      storeId: request.storeId,
      permissions: request.permissions,
      createdAt: new Date().toISOString(),
      createdByUserId: 1,
    };
    mockPermissions.push(newPermission);
    return newPermission;
  }

  /** Remove folder permission */
  async removeFolderPermission(permissionId: number): Promise<void> {
    await delay(150);
    const index = mockPermissions.findIndex(p => p.id === permissionId);
    if (index !== -1) {
      mockPermissions.splice(index, 1);
    }
  }

  /**
   * Get or create task evidence folder using HYBRID structure:
   * /[Franchise]/Stores/[Store]/Task Evidence/[Task Template]/[Date]
   *
   * This structure provides:
   * - Store-level grouping for store managers
   * - Template organisation for easy auditing by task type
   * - Date-based sub-folders for historical searches
   */
  async getOrCreateTaskEvidenceFolder(
    taskTemplateId: number,
    taskTemplateName: string,
    storeId: number,
    evidenceDate: string,
  ): Promise<DocumentFolderDto> {
    await delay(200);

    // Step 1: Find existing date folder for this task template at this store
    const existingDateFolder = mockFolders.find(f =>
      f.scope === DocumentScope.TaskEvidence &&
      f.taskTemplateId === taskTemplateId &&
      f.storeId === storeId &&
      f.evidenceDate === evidenceDate,
    );

    if (existingDateFolder) return existingDateFolder;

    // Step 2: Find or create the store's Task Evidence folder
    let storeEvidenceFolder = mockFolders.find(f =>
      f.scope === DocumentScope.TaskEvidence &&
      f.category === 'Evidence' &&
      f.storeId === storeId &&
      (f.taskTemplateId === undefined || f.taskTemplateId === null) &&
      f.name === 'Task Evidence',
    );

    if (!storeEvidenceFolder) {
      // Find the store folder first
      const storeFolder = mockFolders.find(f =>
        f.scope === DocumentScope.Store &&
        f.storeId === storeId &&
        f.category === 'General',
      );

      if (!storeFolder) {
        throw new ApiError(404, 'Store folder not found');
      }

      storeEvidenceFolder = await this.createFolder({
        name: 'Task Evidence',
        description: 'Evidence captured from completed tasks',
        parentFolderId: storeFolder.id,
        scope: DocumentScope.TaskEvidence,
        category: DocumentCategory.Evidence,
        franchiseId: storeFolder.franchiseId,
        storeId,
      });
    }

    // Step 3: Find or create the task template folder within Task Evidence
    let templateFolder = mockFolders.find(f =>
      f.scope === DocumentScope.TaskEvidence &&
      f.storeId === storeId &&
      f.taskTemplateId === taskTemplateId &&
      f.parentFolderId === storeEvidenceFolder!.id &&
      !f.evidenceDate, // Template folder doesn't have a date
    );

    if (!templateFolder) {
      templateFolder = await this.createFolder({
        name: taskTemplateName,
        description: `Evidence from ${taskTemplateName} tasks`,
        parentFolderId: storeEvidenceFolder.id,
        scope: DocumentScope.TaskEvidence,
        category: DocumentCategory.Evidence,
        franchiseId: storeEvidenceFolder.franchiseId,
        storeId,
        taskTemplateId,
      });
    }

    // Step 4: Create the date folder within the template folder
    const dateFolder = await this.createFolder({
      name: evidenceDate,
      description: `Evidence for ${taskTemplateName} on ${evidenceDate}`,
      parentFolderId: templateFolder.id,
      scope: DocumentScope.TaskEvidence,
      category: DocumentCategory.Evidence,
      franchiseId: templateFolder.franchiseId,
      storeId,
      taskTemplateId,
    });

    // Set the evidence date on the folder
    dateFolder.evidenceDate = evidenceDate;

    return dateFolder;
  }
}

/* ---- Documents Client (Mocked) ---- */
export class DocumentsClientMocked implements IDocumentsClient {
  /** Get documents in a folder (returns latest version info by default) */
  async getDocuments(folderId: number): Promise<DocumentDto[]> {
    await delay(150);
    return mockDocuments.filter(d => d.folderId === folderId);
  }

  /** Get a single document by ID (includes latest version) */
  async getDocument(id: number): Promise<DocumentDto> {
    await delay(100);
    const doc = mockDocuments.find(d => d.id === id);
    if (!doc) throw new ApiError(404, 'Document not found');
    return doc;
  }

  /** Get a document with all version history */
  async getDocumentWithVersions(id: number): Promise<DocumentDto> {
    await delay(150);
    const doc = mockDocuments.find(d => d.id === id);
    if (!doc) throw new ApiError(404, 'Document not found');

    // Populate all versions from mockDocumentVersions
    const versions = mockDocumentVersions.filter(v => v.documentId === id);
    return {
      ...doc,
      versions: versions.sort((a, b) => b.versionNumber - a.versionNumber),
    };
  }

  /** Get a specific version of a document */
  async getDocumentVersion(documentId: number, versionNumber: number): Promise<DocumentVersionDto> {
    await delay(100);
    const version = mockDocumentVersions.find(
      v => v.documentId === documentId && v.versionNumber === versionNumber,
    );
    if (!version) throw new ApiError(404, 'Document version not found');
    return version;
  }

  /** Search documents */
  async searchDocuments(params: DocumentSearchParams): Promise<DocumentSearchResult> {
    await delay(250);

    let filtered = [...mockDocuments];

    if (params.folderId) {
      filtered = filtered.filter(d => d.folderId === params.folderId);
    }

    if (params.query) {
      const q = params.query.toLowerCase();
      filtered = filtered.filter(d =>
        d.name.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        d.tags.some(t => t.toLowerCase().includes(q)),
      );
    }

    if (params.documentType) {
      filtered = filtered.filter(d => d.latestVersion.documentType === params.documentType);
    }

    if (params.tags && params.tags.length > 0) {
      filtered = filtered.filter(d =>
        params.tags!.some(t => d.tags.includes(t)),
      );
    }

    if (params.taskTemplateId) {
      filtered = filtered.filter(d =>
        d.linkedTaskTemplateIds?.includes(params.taskTemplateId!),
      );
    }

    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const start = (page - 1) * pageSize;
    const paged = filtered.slice(start, start + pageSize);

    return {
      documents: paged,
      totalCount: filtered.length,
      page,
      pageSize,
    };
  }

  /** Upload a new document (creates document with first version) */
  async uploadDocument(request: UploadDocumentRequest, _file: File): Promise<DocumentDto> {
    await delay(500);

    const folder = mockFolders.find(f => f.id === request.folderId);
    if (!folder) throw new ApiError(404, 'Folder not found');

    const documentId = idGenerators.nextDocumentId++;
    const versionId = idGenerators.nextVersionId++;
    const now = new Date().toISOString();

    // Create the first version
    const firstVersion: DocumentVersionDto = {
      id: versionId,
      documentId,
      versionNumber: 1,
      fileName: _file.name,
      fileSize: _file.size,
      mimeType: _file.type,
      documentType: getDocumentTypeFromMime(_file.type),
      blobUrl: `/mock-documents/uploads/${_file.name}`,
      thumbnailUrl: _file.type.startsWith('image/') ? `/mock-documents/uploads/${_file.name}` : undefined,
      changeNotes: 'Initial upload',
      createdAt: now,
      createdByUserId: 1,
      createdByUserName: 'Current User',
    };
    mockDocumentVersions.push(firstVersion);

    // Create the document wrapper
    const newDoc: DocumentDto = {
      id: documentId,
      folderId: request.folderId,
      name: request.name,
      description: request.description,
      tags: request.tags ?? [],
      linkedTaskTemplateIds: request.linkedTaskTemplateIds,
      versionCount: 1,
      latestVersion: firstVersion,
      createdAt: now,
      createdByUserId: 1,
      createdByUserName: 'Current User',
    };

    mockDocuments.push(newDoc);
    folder.documentCount++;

    return newDoc;
  }

  /** Upload a new version of an existing document */
  async uploadVersion(request: UploadVersionRequest, _file: File): Promise<DocumentDto> {
    await delay(500);

    const doc = mockDocuments.find(d => d.id === request.documentId);
    if (!doc) throw new ApiError(404, 'Document not found');

    const versionId = idGenerators.nextVersionId++;
    const now = new Date().toISOString();
    const newVersionNumber = doc.versionCount + 1;

    // Create the new version
    const newVersion: DocumentVersionDto = {
      id: versionId,
      documentId: request.documentId,
      versionNumber: newVersionNumber,
      fileName: _file.name,
      fileSize: _file.size,
      mimeType: _file.type,
      documentType: getDocumentTypeFromMime(_file.type),
      blobUrl: `/mock-documents/uploads/v${newVersionNumber}/${_file.name}`,
      thumbnailUrl: _file.type.startsWith('image/') ? `/mock-documents/uploads/v${newVersionNumber}/${_file.name}` : undefined,
      changeNotes: request.changeNotes,
      createdAt: now,
      createdByUserId: 1,
      createdByUserName: 'Current User',
    };
    mockDocumentVersions.push(newVersion);

    // Update the document wrapper
    doc.versionCount = newVersionNumber;
    doc.latestVersion = newVersion;
    doc.updatedAt = now;
    doc.updatedByUserId = 1;

    return doc;
  }

  /** Update document metadata (not the file) */
  async updateDocument(id: number, request: UpdateDocumentRequest): Promise<DocumentDto> {
    await delay(200);
    const doc = mockDocuments.find(d => d.id === id);
    if (!doc) throw new ApiError(404, 'Document not found');

    doc.name = request.name;
    doc.description = request.description;
    doc.tags = request.tags ?? doc.tags;
    doc.linkedTaskTemplateIds = request.linkedTaskTemplateIds;
    doc.updatedAt = new Date().toISOString();
    doc.updatedByUserId = 1;

    return doc;
  }

  /** Delete a document and all its versions */
  async deleteDocument(id: number): Promise<void> {
    await delay(200);
    const index = mockDocuments.findIndex(d => d.id === id);
    if (index === -1) throw new ApiError(404, 'Document not found');

    const doc = mockDocuments[index];
    const folder = mockFolders.find(f => f.id === doc.folderId);
    if (folder) folder.documentCount--;

    // Remove all versions
    for (let i = mockDocumentVersions.length - 1; i >= 0; i--) {
      if (mockDocumentVersions[i].documentId === id) {
        mockDocumentVersions.splice(i, 1);
      }
    }

    mockDocuments.splice(index, 1);
  }

  /** Store task evidence (combines folder creation and document upload) */
  async storeTaskEvidence(
    request: StoreTaskEvidenceRequest,
    file: File,
    taskTemplateName: string,
  ): Promise<DocumentDto> {
    const foldersClient = new DocumentFoldersClientMocked();

    const folder = await foldersClient.getOrCreateTaskEvidenceFolder(
      request.taskTemplateId,
      taskTemplateName,
      request.storeId,
      request.evidenceDate,
    );

    return this.uploadDocument(
      {
        folderId: folder.id,
        name: request.description ?? `${request.evidenceType} - ${new Date().toISOString()}`,
        description: request.description,
        tags: ['evidence', request.evidenceType],
        linkedTaskTemplateIds: [request.taskTemplateId],
      },
      file,
    );
  }

  /** Get documents linked to a task template */
  async getDocumentsForTaskTemplate(taskTemplateId: number): Promise<DocumentDto[]> {
    await delay(150);
    return mockDocuments.filter(d =>
      d.linkedTaskTemplateIds?.includes(taskTemplateId),
    );
  }
}
